/**
 * ProjectBriefPanel — a project's picture on the Monday weekly plan card (the progress update), where its
 * Client Manager is already thinking about it (Matt, 2026-10-25: "these feel like the cards that should
 * show me their end target and used buffer"; the weekly plan, docs/ALDERFLOW.md §5c3).
 *
 * In order: the Build Ready target (set it, or move it for work added since), anything STALE (open work
 * past its date: post it, dated, or say it's still going), what is in the air, what is ready to start, and
 * notes. A tap on a ready milestone or a typed line becomes a note for the crewing step (database/346).
 */
import { useEffect, useState } from 'react'
import { ctx } from '../outline/ctx.js'
import { getProjectBrief, addTriageNote, setProjectTarget } from '../services/alderflow.js'
import { setGateItemStatus } from '../services/gates.js'
import BuildReadyStrip from './BuildReadyStrip.jsx'

const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

export default function ProjectBriefPanel({ projectId, projectName, source = 'progress' }) {
  const [b, setB] = useState(null)        // null loading, false failed
  const [text, setText] = useState('')
  const [busy, setBusy] = useState('')
  const [noted, setNoted] = useState([])  // notes added on this card
  const [going, setGoing] = useState({})  // stale milestone id -> answered "still going"
  const [ownDate, setOwnDate] = useState('')
  const [err, setErr] = useState('')
  const [kitDone, setKitDone] = useState({})   // kit item id -> 'In hand' | 'Not required'
  const [nrFor, setNrFor] = useState(null)     // kit item id whose not-required reason is being typed
  const [nrText, setNrText] = useState('')
  const load = () => getProjectBrief(projectId).then(r => setB(r || false)).catch(() => setB(false))
  useEffect(() => {
    let alive = true
    setB(null); setNoted([]); setText(''); setGoing({}); setOwnDate(''); setErr('')
    if (projectId) getProjectBrief(projectId).then(r => { if (alive) setB(r || false) }).catch(() => { if (alive) setB(false) })
    // A post made from here comes back as a real post: read the project again.
    const onClosed = (e) => { if (alive && e.detail && e.detail.posted) load() }
    window.addEventListener('post-ms-modal-closed', onClosed)
    return () => { alive = false; window.removeEventListener('post-ms-modal-closed', onClosed) }
  }, [projectId])   // eslint-disable-line react-hooks/exhaustive-deps
  if (!projectId) return null
  const me = ctx.currentMember
  const who = { memberId: me && me.id, memberName: me && me.fields ? (me.fields['Member Name'] || me.fields['Name'] || null) : null }
  const br = b && b.buildReady

  async function note(t, ms) {
    const line = String(t || '').trim()
    if (!line) return
    setBusy(ms ? ms.id : 'text')
    try {
      await addTriageNote({ projectId, projectName, milestoneId: ms ? ms.id : null, milestoneName: ms ? ms.name : null, text: line, source, ...who })
      setNoted(n => [...n, { text: line, milestoneId: ms ? ms.id : null }])
      if (!ms) setText('')
    } finally { setBusy('') }
  }
  async function target(date, reason) {
    setBusy('target'); setErr('')
    try {
      await setProjectTarget({ projectId, projectName, date, chainDays: br.chainDays, bufferDays: reason && br.scopeAdded && date === br.scopeAdded.newDate ? (br.target.bufferDays || 0) + br.scopeAdded.buffer : br.target ? br.target.bufferDays : br.bufferDays,
        basis: { split: br.split, path: br.path, estimated: br.estimated }, reason: reason || null, ...who })
      await load()
    } catch (e) { setErr((e && e.message) || 'The Build Ready date did not save.') } finally { setBusy('') }
  }
  const post = (m) => window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: m.id, zIndex: 1000006, instruction: `${m.name} was due ${fmt(m.due)}. Post it dated the day it was really finished.` } }))
  const notedMs = new Set([...(b && b.notes ? b.notes : []), ...noted].map(n => n.milestoneId || n.milestone_airtable_id).filter(Boolean))
  const stale = b && b.stale ? b.stale.filter(m => !going[m.id]) : []

  return (
    <div style={S.wrap}>
      <BuildReadyStrip projectId={projectId} compact />
      {b === null && <div style={S.dim}>Reading the project…</div>}
      {b && br && br.eligible && !br.reached && !br.target && br.proposed && (
        <div style={S.line}>
          <span style={S.k}>Set the Build Ready date</span>
          <button type="button" style={S.act} disabled={!!busy} onClick={() => target(br.proposed)}>{busy === 'target' ? 'Saving…' : `Hold to ${fmt(br.proposed)}`}</button>
          <span style={S.dim}> or </span>
          <input type="date" value={ownDate} onChange={e => setOwnDate(e.target.value)} style={S.date} aria-label="A different Build Ready date" />
          {ownDate && <button type="button" style={S.act} disabled={!!busy} onClick={() => target(ownDate, 'Set by the Client Manager')}>Set {fmt(ownDate)}</button>}
          <div style={S.dim}>{br.chainDays} working days of work and waits left, plus a {br.bufferDays}-day buffer.</div>
        </div>
      )}
      {b && br && br.scopeAdded && br.target && (
        <div style={S.line}>
          <span style={S.k}>Work added</span>{br.scopeAdded.milestones.join(', ')} (+{br.scopeAdded.days} working days).{' '}
          <button type="button" style={S.act} disabled={!!busy} onClick={() => target(br.scopeAdded.newDate, `Scope added: ${br.scopeAdded.milestones.join(', ')} (+${br.scopeAdded.days} working days, +${br.scopeAdded.buffer} buffer)`)}>Move the Build Ready date to {fmt(br.scopeAdded.newDate)}</button>
          <button type="button" style={S.ghost} disabled={!!busy} onClick={() => target(br.target.date, `Scope added (${br.scopeAdded.milestones.join(', ')}, +${br.scopeAdded.days} working days), absorbed in the buffer`)}>Keep {fmt(br.target.date)}</button>
        </div>
      )}
      {err && <div style={{ color: '#b0413e' }}>{err}</div>}
      {b && stale.length > 0 && (
        <div style={S.line}>
          <span style={S.k}>Past its date</span>
          {stale.map(m => (
            <div key={m.id} style={S.row}>
              <span>{m.name} <span style={S.dim}>· due {fmt(m.due)}, {m.days} working day{m.days === 1 ? '' : 's'} ago{m.flying ? ' · in the air' : ''}</span></span>
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <button type="button" style={S.act} onClick={() => post(m)}>Post it…</button>
                <button type="button" style={S.ghost} onClick={() => setGoing(g => ({ ...g, [m.id]: true }))}>Still going</button>
              </span>
            </div>
          ))}
        </div>
      )}
      {b && (
        <>
          {b.inAir.length > 0 && (
            <div style={S.line}><span style={S.k}>In the air</span>{b.inAir.map(f => `${f.name}${f.crew.length ? ' (' + f.crew.join(', ') + ')' : ''}${f.holding ? ', holding' : ''}`).join(' · ')}</div>
          )}
          {(b.kit || []).length > 0 && (
            <div style={S.line}>
              <span style={S.k}>Kit for what’s next</span>
              {b.kit.map(k => (
                <div key={k.milestoneId} style={{ marginTop: 3 }}>
                  <div style={S.dim}>{k.milestoneName}</div>
                  {k.items.map(it => (
                    <div key={it.id} style={S.row}>
                      <span>{it.name} <span style={S.dim}>· {it.role}</span></span>
                      {kitDone[it.id] ? <span style={{ color: '#1f7a4d', fontWeight: 600 }}>{kitDone[it.id]}</span>
                        : nrFor === it.id ? (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <input value={nrText} onChange={e => setNrText(e.target.value)} placeholder="Why it isn’t needed" style={S.input} />
                            <button type="button" style={S.act} disabled={!nrText.trim() || !!busy} onClick={async () => { setBusy(it.id); try { await setGateItemStatus(it.id, 'not_required', { reason: nrText.trim(), ...who }); setKitDone(d => ({ ...d, [it.id]: 'Not required' })); setNrFor(null); setNrText('') } finally { setBusy('') } }}>Save</button>
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <button type="button" style={S.act} disabled={!!busy} onClick={async () => { setBusy(it.id); try { await setGateItemStatus(it.id, 'complete', who); setKitDone(d => ({ ...d, [it.id]: 'In hand' })) } finally { setBusy('') } }}>{busy === it.id ? 'Saving…' : 'In hand'}</button>
                            <button type="button" style={S.ghost} disabled={!!busy} onClick={() => { setNrFor(it.id); setNrText('') }}>Not needed</button>
                          </span>
                        )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {/* "Ready to start" pills and the "Remember for the plan" note box were taken off the weekly plan
              card (Matt, 2026-10-28): the Build Ready line stays. */}
          {b.cards.length > 0 && (
            <div style={S.line}><span style={S.k}>Open questions</span>{b.cards.slice(0, 3).map(c => c.q).join(' · ')}</div>
          )}
        </>
      )}
    </div>
  )
}

const S = {
  wrap: { display: 'grid', gap: 7, margin: '4px 0 12px', padding: '10px 12px', borderRadius: 10, background: 'rgba(0,0,0,0.03)', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#3a3a3c' },
  line: { lineHeight: 1.55 },
  row: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '3px 0', borderTop: '0.5px solid rgba(0,0,0,0.08)' },
  k: { fontWeight: 600, marginRight: 8, color: '#1d1d1f' },
  dim: { color: '#8e8e93' },
  chips: { display: 'inline-flex', flexWrap: 'wrap', gap: 6, verticalAlign: 'middle' },
  chip: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, padding: '3px 10px', borderRadius: 20, border: '0.5px solid #c7c7cc', background: '#fff', color: '#1d1d1f', cursor: 'pointer' },
  chipOn: { background: '#e8f5ee', borderColor: '#b5dcc5', color: '#1f7a4d', cursor: 'default' },
  act: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: 0, background: '#07272D', color: '#fff', cursor: 'pointer' },
  ghost: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, padding: '3px 10px', borderRadius: 6, border: '0.5px solid #c7c7cc', background: '#fff', color: '#3a3a3c', cursor: 'pointer', marginLeft: 6 },
  date: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '0.5px solid #c7c7cc', background: '#fff', color: '#1d1d1f' },
  form: { display: 'flex', gap: 6, marginTop: 2 },
  input: { flex: 1, minWidth: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, padding: '6px 9px', borderRadius: 6, border: '0.5px solid #c7c7cc', background: '#fff', color: '#1d1d1f' },
  save: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, padding: '6px 12px', borderRadius: 6, border: 0, background: '#07272D', color: '#fff', cursor: 'pointer' },
}
